// The shared save / continue bar every wizard step screen puts at its foot
// (Module 02 §3.1, §5, §9.1). It owns the two step mutations and every failure
// mode they have, so no screen re-implements optimistic concurrency.
//
// Contract with the screen:
//  - `getPayload()` returns the step's scratch state; the bar sends it to
//    `PUT /v1/onboarding/steps/{n}/payload`.
//  - `updatedAt` is the `updated_at` the screen LOADED the step with. Sending it
//    turns the save into a compare-and-set: a concurrent editor gets 409
//    `STALE_PAYLOAD`, the bar shows the conflict panel with the server's current
//    state and calls `onStale(serverStep)` so the screen can re-seed its editor.
//    Omit it only for a genuine first save (last-write-wins).
//  - "Mark step complete" calls `POST .../complete`, which validates the step's
//    preconditions and answers 422 `PRECONDITIONS_UNMET` with the unmet keys —
//    rendered as a list, never as a raw code.
//  - Once the tenant is live every wizard mutation answers 409 `ALREADY_LIVE`;
//    pass `frozen` so the bar renders read-only instead of offering a button the
//    server will refuse.

import { useEffect, useState, type ReactNode } from "react";
import { useBlocker, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { Button } from "../../../components/button";
import { useCompleteStep, useSaveStepPayload } from "../hooks/onboarding-queries";
import { useOnboardingPermissions } from "../hooks/permissions";
import { stalePayloadServerStep } from "../lib/wizard-problems";
import {
  canonicalPayload,
  staysWithinStep,
  type OnboardingRoutePath,
  type StepNo,
} from "../lib/wizard-steps";
import { WizardDialog } from "./wizard-dialog";
import { WizardProblemNotice } from "./wizard-notices";

type S = components["schemas"];

export interface SaveContinueBarProps {
  /** The step this bar drives (1–4). */
  stepNo: StepNo;
  /** Build the step's scratch payload at save time. */
  getPayload: () => Record<string, unknown>;
  /** `updated_at` of the loaded step state — enables the 409 STALE_PAYLOAD check. */
  updatedAt?: string | undefined;
  /**
   * The DERIVED scratch the screen seeded its editor from the loaded step — the
   * baseline "has the user changed anything?" is measured against. Sibling to
   * `updatedAt`: both describe the server state the screen was seeded from.
   *
   * Pass the derivation, NEVER the raw `step.payload`: the server seeds a
   * never-saved step's payload as `{}`::jsonb, and `{}` never canonically
   * equals the default scratch the screen seeds from it (`{tab:"manual"}`, …) —
   * so a raw payload makes every untouched fresh step read "Unsaved changes"
   * for the whole first pass of a new tenant (§3.8).
   *
   * Omit only for a step with no server state at all; then the bar falls back to
   * treating any non-empty payload as unsaved work, which is the safe direction
   * but cannot distinguish a draft from an untouched default.
   */
  loadedPayload?: Record<string, unknown> | undefined;
  /** Called with the fresh step state after a successful save. */
  onSaved?: ((step: S["OnboardingStepState"]) => void) | undefined;
  /** Called with the fresh step state after a successful completion. */
  onCompleted?: ((step: S["OnboardingStepState"]) => void) | undefined;
  /** Called with the SERVER's step state on a 409 STALE_PAYLOAD, so the screen
   * can re-seed its editor from it rather than clobbering someone's work. */
  onStale?: ((serverStep: S["OnboardingStepState"]) => void) | undefined;
  /** Where a successful "save and continue" navigates. Omit to stay put. */
  continueTo?: OnboardingRoutePath | undefined;
  /** Hide the "mark step complete" button (screens that only save drafts). */
  showComplete?: boolean;
  /** ONE primary per view (§3.10): a screen whose own CTA is the primary (the
   * plant map's Commit) passes false so Complete renders secondary. */
  completePrimary?: boolean;
  /** The tenant is live — render read-only (§3.1 freeze). */
  frozen?: boolean;
  /** §3.5b pt 6 (D-214, S12): the step has NO payload — hide Save and
   * Save-and-continue, and Complete does NOT run a save first (a confirm-only
   * bar must not side-write `PUT /payload {}`). */
  completeOnly?: boolean;
  /** Extra controls rendered on the left of the bar. */
  children?: ReactNode;
}

export function SaveContinueBar({
  stepNo,
  getPayload,
  updatedAt,
  loadedPayload,
  onSaved,
  onCompleted,
  onStale,
  continueTo,
  showComplete = true,
  completePrimary = true,
  frozen = false,
  completeOnly = false,
  children,
}: SaveContinueBarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const perms = useOnboardingPermissions();
  const save = useSaveStepPayload(stepNo);
  const complete = useCompleteStep(stepNo);
  /** JSON of the payload as the SERVER last accepted it — the dirty baseline. */
  const [savedPayload, setSavedPayload] = useState<string | null>(null);
  /**
   * The `updated_at` a 409 STALE_PAYLOAD handed back. It is preferred over the
   * `updatedAt` prop because the prop comes from the cached session, which still
   * holds the token the server just refused. Without this the recovery loop —
   * "Load the current version" → re-apply edits → Save — 409s forever (§9.1,
   * acceptance M02-S10). The hook also re-reads the session on 409; this is the
   * belt to that braces, and it is what makes the retry work on the FIRST click
   * rather than after a refetch lands.
   */
  const [adoptedUpdatedAt, setAdoptedUpdatedAt] = useState<string | null>(null);

  // §7: saving payloads and completing steps both need onboarding.manage_wizard
  // (group_admin / facility_admin). Fail-closed while the role feed settles.
  const canEdit = perms.has("onboarding.manage_wizard") && !frozen;

  // A payload the user changed since the last accepted save. Drives the honest
  // "Saved" / "Unsaved changes" state AND the navigation guard: telling someone
  // their 15-minute plant map is "Saved" while it is only in React state is the
  // worst possible moment to be wrong.
  //
  // The baseline is the payload the SERVER holds: whatever this browser last
  // saved, or failing that the payload the screen loaded the step with. It used
  // to be `null` until a save happened in this browser session, which made every
  // freshly-loaded step "unsaved" from its first paint, for every user, forever
  // — the step had not been edited, it had merely been opened.
  //
  // There are TWO beliefs about what the server holds, and either one being
  // right means the work is saved:
  //   · `savedPayload` — what THIS bar last had accepted. Covers the window
  //     before the session refetch lands.
  //   · `loadedPayload` — what the session says the step holds now. Covers a
  //     save this bar did not make: another tab, a 409 recovery, or — the case
  //     that forced this — the plant map persisting the step payload as part of
  //     committing, so the footer cannot read "You have unsaved changes." under
  //     a banner saying the plant is committed (UX-1 surface review F8).
  // Preferring `savedPayload` outright made the bar report unsaved work
  // whenever the server moved ahead of this bar's own last save. Both are only
  // ever set from a payload the SERVER accepted, so treating a match against
  // either as clean cannot claim saved for something that is not.
  const currentPayload = canonicalPayload(getPayload());
  const loadedBaseline = loadedPayload === undefined ? null : canonicalPayload(loadedPayload);
  const known = [savedPayload, loadedBaseline].filter((v): v is string => v !== null);
  const dirty = known.length > 0 && !known.includes(currentPayload);
  const neverSaved = known.length === 0;
  const unsaved = canEdit && (dirty || (neverSaved && !isEmptyPayload(currentPayload)));
  /** True when we know SOMETHING about the server's copy of this step — the
   * condition for saying "Saved" out loud rather than staying silent. */
  const hasServerBaseline = known.length > 0;

  // Leaving the page with unsaved work is the §3.8 data-loss hole: the step rail,
  // the header links and browser Back all left the draft behind without a word.
  //
  // Scoped to navigations that actually LEAVE the step (`staysWithinStep`,
  // keyed by (step, destination) — spec 02 §3.8): THIS step opening one of its
  // own screens — the import detail screen for steps 2/4 — is not abandoning a
  // draft, and stopping it there is what made S4 unreachable. A dirty step 1 or
  // 3 owns no screens, so the same destination is still challenged there.
  //
  // Asked through the area's own dialog rather than `window.confirm`. That is
  // the §4 rule the rest of the module already follows (see wizard-dialog.tsx),
  // and it is not cosmetic here: `window.confirm` is auto-dismissed by every
  // automated browser, so the guard's answer was a hard-coded "no, stay" that no
  // walkthrough could drive or even observe. `withResolver` is TanStack Router's
  // documented way to answer a blocker asynchronously.
  const leaving = useBlocker({
    shouldBlockFn: ({ next }) => unsaved && !staysWithinStep(stepNo, next.pathname),
    enableBeforeUnload: () => unsaved,
    withResolver: true,
  });

  useEffect(() => {
    // Adopting the server's token is only meaningful while its 409 is the live
    // error; a fresh successful save supersedes it.
    const serverStep = stalePayloadServerStep(save.error);
    if (serverStep !== undefined) setAdoptedUpdatedAt(serverStep.updated_at);
  }, [save.error]);

  function runSave(): Promise<S["OnboardingStepState"] | undefined> {
    const token = adoptedUpdatedAt ?? updatedAt;
    const payload = getPayload();
    const body: S["OnboardingStepPayloadPutRequest"] =
      token === undefined ? { payload } : { payload, updated_at: token };
    return save
      .mutateAsync(body)
      .then((step) => {
        setSavedPayload(canonicalPayload(payload));
        setAdoptedUpdatedAt(step.updated_at);
        onSaved?.(step);
        return step;
      })
      .catch(() => undefined);
  }

  async function onSaveClick() {
    await runSave();
  }

  // The bar's OWN post-save navigations carry `ignoreBlocker: true` (a documented
  // TanStack `NavigateOptions` field): the save that just succeeded is the very
  // thing the guard exists to protect, but `useBlocker` registers its blockerFn
  // in an effect, so the closure the router holds is from the LAST COMMITTED
  // render — and `navigate()` here runs in the same microtask chain as
  // `setSavedPayload`, before any React commit. `@tanstack/history` invokes the
  // blockerFn synchronously in `tryNavigation`, so without the bypass it
  // evaluates the PRE-save `unsaved === true` and asks "Leave without saving?"
  // right after saving — holding the navigation the user just earned.
  async function onSaveAndContinue() {
    const step = await runSave();
    if (step !== undefined && continueTo !== undefined) {
      await navigate({ to: continueTo, ignoreBlocker: true });
    }
  }

  async function onComplete() {
    if (!completeOnly) {
      const step = await runSave();
      if (step === undefined) return;
    }
    try {
      const completed = await complete.mutateAsync();
      onCompleted?.(completed);
      if (continueTo !== undefined) {
        // Same stale-closure hazard as onSaveAndContinue — see the note there.
        await navigate({ to: continueTo, ignoreBlocker: true });
      }
    } catch {
      // The notice below renders the 422 / 409 the server sent.
    }
  }

  const busy = save.isPending || complete.isPending;
  const failure: unknown = save.error ?? complete.error;

  return (
    <div
      // Rev-7: a confirm-only bar has no unsaved state to keep in view, so it
      // sits in the flow at the end of the step instead of floating over the
      // last controls (the audit's measured overlap on step 5 at 412 px).
      className={completeOnly ? "wizard-save-bar is-static" : "wizard-save-bar"}
      data-testid="save-continue-bar"
      data-dirty={unsaved ? "true" : "false"}
    >
      {failure !== null && failure !== undefined ? (
        <WizardProblemNotice
          error={failure}
          onRetry={() => void onSaveClick()}
          onReloadServerStep={(serverStep) => {
            // Adopt the server's version AND its concurrency token together, so
            // the very next Save is a compare-and-set against what is actually
            // stored rather than a repeat of the refused one.
            setAdoptedUpdatedAt(serverStep.updated_at);
            setSavedPayload(canonicalPayload(serverStep.payload));
            save.reset();
            onStale?.(serverStep);
          }}
          titleKey="onboarding.save.failedTitle"
          detailKey="onboarding.save.failedDetail"
        />
      ) : null}
      <div className="wizard-save-bar-row">
        <div className="wizard-save-bar-left">
          {children}
          {!canEdit ? (
            <p className="wizard-save-bar-note">{t("onboarding.save.readOnly")}</p>
          ) : busy ? null : unsaved ? (
            <p className="wizard-save-bar-note is-dirty" role="status" data-testid="save-dirty">
              {t("onboarding.save.unsaved")}
            </p>
          ) : hasServerBaseline ? (
            // A step loaded and not since edited IS saved — the server holds
            // exactly this payload — so say so, rather than staying silent until
            // the user happens to press Save.
            <p className="wizard-save-bar-note" role="status" data-testid="save-clean">
              {t("onboarding.save.saved")}
            </p>
          ) : null}
        </div>
        <div className="wizard-save-bar-actions">
          {completeOnly ? null : (
            <Button
              onClick={() => void onSaveClick()}
              busy={save.isPending}
              disabled={!canEdit || busy || (!unsaved && savedPayload !== null)}
            >
              {save.isPending ? t("onboarding.save.saving") : t("onboarding.save.save")}
            </Button>
          )}
          {continueTo !== undefined && !completeOnly ? (
            <Button onClick={() => void onSaveAndContinue()} disabled={!canEdit || busy}>
              {t("onboarding.save.saveAndContinue")}
            </Button>
          ) : null}
          {showComplete ? (
            <Button
              variant={completePrimary ? "primary" : "secondary"}
              onClick={() => void onComplete()}
              busy={complete.isPending}
              disabled={!canEdit || busy}
            >
              {complete.isPending
                ? t("onboarding.save.completing")
                : t("onboarding.save.completeStep")}
            </Button>
          ) : null}
        </div>
      </div>

      {/* The unsaved-work question, asked so it can be read, translated,
          keyboard-driven and walked through. Both dismissal paths the dialog
          offers on its own (Escape, scrim click) land on `reset` — staying put
          with the draft intact is always the safe outcome. */}
      {leaving.status === "blocked" ? (
        <WizardDialog
          testId="leave-unsaved-confirm"
          destructive
          title={t("onboarding.save.leaveTitle")}
          confirmLabel={t("onboarding.save.leaveDiscard")}
          cancelLabel={t("onboarding.save.leaveStay")}
          onConfirm={leaving.proceed}
          onCancel={leaving.reset}
        >
          <p>{t("onboarding.save.leaveBody")}</p>
        </WizardDialog>
      ) : null}
    </div>
  );
}

/**
 * A payload with nothing in it is not "unsaved work". Empty means no members,
 * or every member empty/blank — `{facilities: []}` is empty, `{tab: "manual"}`
 * is NOT (it has a non-blank member).
 *
 * This only matters in the no-`loadedPayload` fallback (a step with no server
 * row at all, §3.8): there the bar deliberately errs toward safety and treats
 * ANY non-empty payload as unsaved, because it has no baseline to compare
 * against — so an untouched default like `{tab:"manual"}` DOES read "Unsaved
 * changes" and arm the guard in that fallback. Screens that load a server step
 * avoid this by passing the DERIVED scratch as `loadedPayload`.
 */
function isEmptyPayload(json: string): boolean {
  try {
    const value: unknown = JSON.parse(json);
    if (value === null || typeof value !== "object") return true;
    return Object.values(value as Record<string, unknown>).every(
      (v) =>
        v === null ||
        v === undefined ||
        v === "" ||
        (Array.isArray(v) && v.length === 0) ||
        (typeof v === "object" && Object.keys(v).length === 0),
    );
  } catch {
    return true;
  }
}
